<template>
  <li class="no-marker">
    <!-- 面包屑导航 -->
    <div v-if="level === 0 && breadcrumbs.length > 0" class="breadcrumbs">
      <span class="breadcrumb-item" @click="navigateToRoot">
        <svg class="breadcrumb-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
        根目录
      </span>
      <template v-for="(crumb, idx) in breadcrumbs" :key="idx">
        <svg class="breadcrumb-separator" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span class="breadcrumb-item" :class="{ active: idx === breadcrumbs.length - 1 }"
          @click="navigateToBreadcrumb(idx)">
          {{ crumb }}
        </span>
      </template>
    </div>

    <!-- 文档树项目 -->
    <transition-group name="tree-list" tag="ul" class="tree-list">
      <template v-for="(item, idx) in finalItems" :key="getKey(item, idx)">
        <!-- 返回上级 -->
  <li v-if="item.isParent" class="tree-item parent-item" @click.stop="onClickParent">
    <div class="tree-item-content">
      <svg class="item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="15 18 9 12 15 6" />
      </svg>
      <span class="item-title">返回上一级</span>
    </div>
  </li>

  <!-- vpubmd特殊处理 -->
  <li v-else-if="item.title === 'vpubmd'" class="tree-item special-item">
    <div class="tree-item-content" @click.stop="onClickItem(item)">
      <svg class="item-icon folder" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      </svg>
      <span class="item-title">{{ item.title }}</span>
      <span class="item-badge">核心文档</span>
    </div>
  </li>

  <!-- 隐藏index -->
  <template v-else-if="item.title === 'index' || item.title === 'dir'"></template>

  <!-- 普通项目 -->
  <li v-else class="tree-item">
    <div class="tree-item-content" :class="{ active: isActive(item) }" @click.stop="onClickItem(item)">

      <!-- 图标 -->
      <svg v-if="isDirectory(item)" class="item-icon folder" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      </svg>
      <svg v-else class="item-icon file" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>

      <span class="item-title">{{ item.title }}</span>
    </div>
  </li>
</template>
</transition-group>
</li>
</template>

<script setup>
import { defineProps, defineEmits, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'

const props = defineProps({
  items: {
    type: Array,
    default: () => []
  },
  selectedItem: {
    type: Object,
    default: () => ({})
  },
  level: {
    type: Number,
    default: 0
  },
  searchTerm: {
    type: String,
    default: ''
  }
})

const emits = defineEmits(['select'])
const router = useRouter()
const route = useRoute()

// 计算面包屑
const breadcrumbs = computed(() => {
  const path = route.query.path || ''
  return path.split('/').filter(s => s)
})

function navigateToRoot() {
  router.push({
    path: route.path,
    query: {}
  })
}

function navigateToBreadcrumb(idx) {
  const newPath = breadcrumbs.value.slice(0, idx + 1).join('/')
  const newQuery = { ...route.query }
  delete newQuery.name
  newQuery.path = newPath

  router.push({
    path: route.path,
    query: newQuery
  })
}

function onClickItem(item) {
  emits('select', item)
}

function isDirectory(item) {
  return item.type === 'dir' || (item.children && item.children.length > 0)
}

function isActive(item) {
  return props.selectedItem?.path && item.path && props.selectedItem.path === item.path
}

function getKey(item, idx) {
  if (item.path) return item.path
  if (item.isParent) return 'parent-dir'
  return `lvl${props.level}-idx${idx}-${item.title}`
}

// 递归过滤
function filterItems(arr, keyword) {
  if (!keyword) return arr
  return arr.filter(it => it.title?.toLowerCase().includes(keyword.toLowerCase()))
}

const filteredItems = computed(() => {
  return filterItems(props.items, props.searchTerm)
})

const finalItems = computed(() => {
  const base = filteredItems.value

  if (props.level !== 0) {
    return base
  }

  let path = route.query.path || ''
  const segs = path.split('/').filter(s => !!s)
  if (segs.length === 0) {
    return base
  }

  const parentItem = { title: '..', isParent: true }
  return [parentItem, ...base]
})

function onClickParent() {
  let path = route.query.path || ''
  let segs = path.split('/').filter(s => s)
  if (segs.length > 0) {
    segs.pop()
    path = segs.join('/')
  }

  const newQuery = { ...route.query }
  delete newQuery.name
  newQuery.path = path

  router.push({
    path: route.path,
    query: newQuery
  })
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.no-marker {
  list-style: none;
  padding: 0;
  margin: 0;
}

/* 面包屑导航 */
.breadcrumbs {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 6px;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 4px;
}

.breadcrumb-item {
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 4px;
}

.breadcrumb-item:hover {
  color: #60a5fa;
  background: rgba(96, 165, 250, 0.1);
}

.breadcrumb-item.active {
  color: #fff;
  cursor: default;
}

.breadcrumb-item.active:hover {
  background: transparent;
}

.breadcrumb-icon {
  width: 16px;
  height: 16px;
}

.breadcrumb-separator {
  width: 16px;
  height: 16px;
  color: #4b5563;
}

/* 树列表 */
.tree-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

/* 树项目 */
.tree-item {
  margin: 1px 0;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.tree-item-content {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s ease;
  position: relative;
  gap: 8px;
}

.tree-item-content:hover {
  background: rgba(96, 165, 250, 0.08);
  transform: translateX(1px);
}

.tree-item-content.active {
  background: linear-gradient(90deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.05) 100%);
  color: #fff;
  border-left: 3px solid #3b82f6;
  padding-left: 8px;
  font-weight: 500;
}

/* 特殊项目样式 */
.parent-item .tree-item-content {
  color: #94a3b8;
  font-style: italic;
}

.parent-item .tree-item-content:hover {
  background: rgba(148, 163, 184, 0.1);
  color: #cbd5e1;
}

.special-item .tree-item-content {
  background: rgba(168, 85, 247, 0.05);
  border: 1px solid rgba(168, 85, 247, 0.15);
}

/* 图标 */
.folder-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 4px;
}

.toggle-icon {
  width: 16px;
  height: 16px;
  color: #6b7280;
  transition: transform 0.2s ease;
}

.toggle-icon.rotated {
  transform: rotate(90deg);
}

.item-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.item-icon.folder {
  color: #fbbf24;
}

.item-icon.file {
  color: #60a5fa;
}

.item-title {
  color: white;
  flex: 1;
  font-size: 14px;
  line-height: 1.5;
}

.item-count {
  background: rgba(255, 255, 255, 0.05);
  color: #9ca3af;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  margin-left: auto;
}

.item-badge {
  background: rgba(168, 85, 247, 0.15);
  color: #a855f7;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  margin-left: 8px;
  border: 1px solid rgba(168, 85, 247, 0.2);
}

/* 子树 */
.tree-children {
  margin-left: 20px;
  padding-left: 10px;
  border-left: 1px solid rgba(255, 255, 255, 0.03);
  list-style: none;
}

/* 动画 - 只在展开时有动画，收起时立即消失 */
.folder-expand-enter-active {
  transition: all 0.25s ease-out;
  transform-origin: top;
}

.folder-expand-leave-active {
  transition: none;
  display: none;
}

.folder-expand-enter-from {
  opacity: 0;
  transform: scaleY(0.95) translateY(-5px);
}

.folder-expand-leave-to {
  opacity: 0;
  display: none;
}

/* 列表动画优化 */
.tree-list {
  position: relative;
}

.tree-list-enter-active,
.tree-list-leave-active {
  transition: all 0.2s ease-out;
}

.tree-list-enter-from {
  opacity: 0;
  transform: translateY(5px);
}

.tree-list-leave-to {
  opacity: 0;
  transform: translateY(-5px);
  position: absolute;
  width: 100%;
}

/* 响应式 */
@media (max-width: 768px) {
  .tree-children {
    margin-left: 16px;
    padding-left: 8px;
  }
}
</style>
